import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import TroubleshootRoundedIcon from '@mui/icons-material/TroubleshootRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { type ReactNode } from 'react'
import type { Workspace, WorkspaceRole } from '../../../context/WorkspaceContext'
import { roleCanPerform } from '../../../utils/workspacePermissions'
import { useNls } from '../../../nls/nlsProvider'
import type { AnalysisIncludedSystemKey, AnalysisSnapshot, AnalysisTimeRangeKey } from '../../../types/analysis'
import { MetricCard } from '../MetricCard'

import { firebaseAuth } from '../../../services/firebase'
import {
  isSparklineAnomaly,
  LiveModuleCard,
  useClarificationModule,
  useLowConfidenceModule,
  useProposalUsageModule,
  usePulseEventsModule,
  useRecommendationTrendsModule,
  useRetrievalHealthModule,
  useWorkspacePulseEvents,
} from '../modules'
import { buildLiveModuleDescriptor, buildMetricDescriptor } from '../popover/descriptors'

import { formatNumber } from './analysisTabShared'
import type { AnalysisNavigate } from './analysisTabShared'

export const AiIntelligenceTab = ({
  workspace,
  role,
  timeRange,
  snapshot,
  snapshotNonce,
  onNavigate,
  onOpenReports,
  includedSystems,
  highlightAnomalies,
}: {
  workspace: Workspace
  role: WorkspaceRole
  timeRange: AnalysisTimeRangeKey
  snapshot: AnalysisSnapshot
  snapshotNonce: number
  onNavigate: AnalysisNavigate
  onOpenReports: () => void
  includedSystems?: Set<AnalysisIncludedSystemKey>
  highlightAnomalies?: boolean
}) => {
  const { t } = useNls()

  // 1. Shared pulse events subscription (Cross-phase rule 1: refreshKey wired to snapshotNonce)
  const {
    events: pulseEvents,
    loading: pulseLoading,
    error: pulseError,
  } = useWorkspacePulseEvents({
    workspaceId: workspace.id,
    refreshKey: snapshotNonce,
  })

  // 2. The 6 AI Intelligence live modules
  const proposalUsage = useProposalUsageModule({
    workspaceId: workspace.id,
    uid: firebaseAuth.currentUser?.uid,
    canApprove: roleCanPerform(role, 'EDITOR'),
    timeRange,
    refreshKey: snapshotNonce,
  })

  const lowConfidence = useLowConfidenceModule({
    pulseEvents,
    timeRange,
    loading: pulseLoading,
    error: pulseError,
  })

  const clarification = useClarificationModule({
    pulseEvents,
    timeRange,
    loading: pulseLoading,
    error: pulseError,
  })

  const retrievalHealth = useRetrievalHealthModule({
    workspaceId: workspace.id,
    timeRange,
    refreshKey: snapshotNonce,
  })

  const recommendationTrends = useRecommendationTrendsModule({
    workspaceId: workspace.id,
    pulseEvents,
    timeRange,
    refreshKey: snapshotNonce,
  })

  const pulseEventsModule = usePulseEventsModule({
    pulseEvents,
    timeRange,
    loading: pulseLoading,
    error: pulseError,
  })

  const aiMetrics: Array<{
    title: string
    value: string
    detail: string
    icon: ReactNode
    tone?: 'default' | 'warn' | 'good'
    numeric: number
  }> = [
    {
      title: 'AI-touched records',
      value: formatNumber(snapshot.ai.generatedCandidates),
      detail: 'Local status-origin markers',
      icon: <AutoAwesomeRoundedIcon />,
      numeric: snapshot.ai.generatedCandidates,
    },
    {
      title: 'Low-confidence patterns',
      value: formatNumber(snapshot.ai.lowConfidenceSignals),
      detail: 'Blocked and stalled signals',
      icon: <WarningAmberRoundedIcon />,
      tone: snapshot.ai.lowConfidenceSignals ? ('warn' as const) : ('good' as const),
      numeric: snapshot.ai.lowConfidenceSignals,
    },
  ]
  if (includedSystems && !includedSystems.has('AI')) {
    return (
      <div className="analysisFilteredStateEmpty" data-testid="ai-filtered-empty">
        <strong>{t('NLSAnalysisFilteredEmptyHeading')}</strong>
        <p>{t('NLSAnalysisFilteredEmptyDesc')}</p>
      </div>
    )
  }
  return (
    <div className="analysisStageStack">
      <div className="analysisMetricGrid">
        {aiMetrics.map((metric) => (
          <MetricCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            detail={metric.detail}
            icon={metric.icon}
            tone={metric.tone}
            descriptor={buildMetricDescriptor({
              snapshot,
              tab: 'AI_INTELLIGENCE',
              title: metric.title,
              value: metric.numeric,
              detail: metric.detail,
              drillTarget: null,
              onNavigate,
              onOpenReports,
            })}
          />
        ))}
      </div>
      <div className="analysisPhaseGrid">
        <>
          <LiveModuleCard
            icon={<AutoAwesomeRoundedIcon />}
            title={t('NLSAnalysisModuleProposalUsageTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowAiIntelligence')}
            headline={proposalUsage.headline}
            sparkline={proposalUsage.sparkline}
            records={proposalUsage.records}
            loading={proposalUsage.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(proposalUsage.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `proposal-usage-${workspace.id}`,
              title: 'Proposal Usage',
              tab: 'AI_INTELLIGENCE',
              cardLabel: 'Proposal Usage',
              points: proposalUsage.sparkline.points,
              records: proposalUsage.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: proposalUsage.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<WarningAmberRoundedIcon />}
            title={t('NLSAnalysisModuleLowConfidenceTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowAiIntelligence')}
            headline={lowConfidence.headline}
            sparkline={lowConfidence.sparkline}
            records={lowConfidence.records}
            loading={lowConfidence.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(lowConfidence.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `low-confidence-${workspace.id}`,
              title: 'Low-Confidence',
              tab: 'AI_INTELLIGENCE',
              cardLabel: 'Low-Confidence',
              points: lowConfidence.sparkline.points,
              records: lowConfidence.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: lowConfidence.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<TroubleshootRoundedIcon />}
            title={t('NLSAnalysisModuleClarificationTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowAiIntelligence')}
            headline={clarification.headline}
            sparkline={clarification.sparkline}
            records={clarification.records}
            loading={clarification.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(clarification.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `clarification-${workspace.id}`,
              title: 'Clarification',
              tab: 'AI_INTELLIGENCE',
              cardLabel: 'Clarification',
              points: clarification.sparkline.points,
              records: clarification.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: clarification.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<HubOutlinedIcon />}
            title={t('NLSAnalysisModuleRetrievalHealthTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowAiIntelligence')}
            headline={retrievalHealth.headline}
            sparkline={retrievalHealth.sparkline}
            records={retrievalHealth.records}
            loading={retrievalHealth.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(retrievalHealth.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `retrieval-health-${workspace.id}`,
              title: 'Retrieval Health',
              tab: 'AI_INTELLIGENCE',
              cardLabel: 'Retrieval Health',
              points: retrievalHealth.sparkline.points,
              records: retrievalHealth.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: retrievalHealth.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<InsightsRoundedIcon />}
            title={t('NLSAnalysisModuleRecommendationTrendsTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowAiIntelligence')}
            headline={recommendationTrends.headline}
            sparkline={recommendationTrends.sparkline}
            records={recommendationTrends.records}
            loading={recommendationTrends.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(recommendationTrends.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `recommendation-trends-${workspace.id}`,
              title: 'Recommendation Trends',
              tab: 'AI_INTELLIGENCE',
              cardLabel: 'Recommendation Trends',
              points: recommendationTrends.sparkline.points,
              records: recommendationTrends.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: recommendationTrends.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<BoltRoundedIcon />}
            title={t('NLSAnalysisModulePulseEventsTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowAiIntelligence')}
            headline={pulseEventsModule.headline}
            sparkline={pulseEventsModule.sparkline}
            records={pulseEventsModule.records}
            loading={pulseEventsModule.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(pulseEventsModule.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `pulse-events-${workspace.id}`,
              title: 'Pulse Events',
              tab: 'AI_INTELLIGENCE',
              cardLabel: 'Pulse Events',
              points: pulseEventsModule.sparkline.points,
              records: pulseEventsModule.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: pulseEventsModule.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
        </>
      </div>
    </div>
  )
}
